"use client";

import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { Send, Check, Loader2 } from "lucide-react";
import {
  contactSchema,
  contactProducts,
  ecommercePlatforms,
  enquiryTypes,
  type ContactFormData,
} from "@/lib/contact/schema";
import { ContactTurnstile } from "./contact-turnstile";

export function ContactForm({ siteKey }: { siteKey: string }) {
  const [honeypot, setHoneypot] = useState("");
  const [token, setToken] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const {
    register,
    handleSubmit,
    control,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      kind: "contact",
      name: "",
      email: "",
      company: "",
      website: "",
      platform: "",
      otherPlatform: "",
      products: [],
      message: "",
    },
  });
  const kind = useWatch({ control, name: "kind" });
  const platform = useWatch({ control, name: "platform" });
  const business = kind !== "contact";
  const labelClass = "block text-sm font-medium text-ink mb-2";
  const field = (name: keyof ContactFormData) => ({
    ...register(name),
    id: `contact-${name}`,
    error: Boolean(errors[name]),
    "aria-invalid": Boolean(errors[name]),
    "aria-describedby": errors[name] ? `contact-${name}-error` : undefined,
  });
  const fieldError = (name: keyof ContactFormData) =>
    errors[name] && (
      <p id={`contact-${name}-error`} className="mt-1 text-sm text-danger">
        {errors[name]?.message}
      </p>
    );

  const onSubmit = async (data: ContactFormData) => {
    if (!token) return;
    setError("");
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          enquiry: {
            ...data,
            ...(data.kind === "contact"
              ? { company: "", website: "", products: [] }
              : {}),
            ...(data.kind !== "brand"
              ? { platform: "", otherPlatform: "" }
              : {}),
            ...(data.platform !== "Other" ? { otherPlatform: "" } : {}),
          },
          token,
          website: honeypot,
        }),
        signal: AbortSignal.timeout(20000),
      });
      const result: unknown = await response.json();
      if (
        response.ok &&
        result &&
        typeof result === "object" &&
        "submitted" in result &&
        result.submitted === true
      ) {
        setSubmitted(true);
      } else {
        setError(
          result &&
            typeof result === "object" &&
            "error" in result &&
            typeof result.error === "string"
            ? result.error
            : "We could not confirm sending. Please email support@buddify.app.",
        );
      }
    } catch {
      setError(
        "We could not confirm sending. Your message may have been sent. Please wait before retrying, or email support@buddify.app.",
      );
    } finally {
      setToken("");
      setAttempt((value) => value + 1);
    }
  };

  if (submitted)
    return (
      <div
        role="status"
        className="text-center py-12"
        tabIndex={-1}
        ref={(element) => element?.focus()}
      >
        <div className="w-16 h-16 bg-brand-green-soft rounded-full flex items-center justify-center mx-auto mb-4">
          <Check aria-hidden="true" className="w-8 h-8 text-brand-green" />
        </div>
        <h2 className="text-h3 text-ink mb-2">Thanks for reaching out.</h2>
        <p className="text-muted">
          Your enquiry has been submitted. Our team will reply to your email.
        </p>
      </div>
    );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
      <fieldset disabled={isSubmitting} className="space-y-6 min-w-0">
        <legend className="text-h3 text-ink mb-6">Let’s talk</legend>
        <div>
          <label htmlFor="contact-kind" className={labelClass}>
            What brings you here?
          </label>
          <Select
            {...field("kind")}
            className="text-ink"
            onChange={(event) => {
              void register("kind").onChange(event);
              clearErrors();
              setError("");
              if (event.target.value === "contact") {
                setValue("company", "");
                setValue("website", "");
                setValue("products", []);
              }
              if (event.target.value !== "brand") {
                setValue("platform", "");
                setValue("otherPlatform", "");
              }
            }}
          >
            {Object.entries(enquiryTypes).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="contact-name" className={labelClass}>
              Your name
            </label>
            <Input
              {...field("name")}
              autoComplete="name"
              maxLength={120}
              required
              placeholder="Full name"
            />
            {fieldError("name")}
          </div>
          <div>
            <label htmlFor="contact-email" className={labelClass}>
              Email
            </label>
            <Input
              {...field("email")}
              type="email"
              autoComplete="email"
              maxLength={254}
              required
              placeholder="you@yourbrand.com"
            />
            {fieldError("email")}
          </div>
        </div>
        {business && (
          <>
            <div>
              <label htmlFor="contact-company" className={labelClass}>
                Company or brand name
              </label>
              <Input
                {...field("company")}
                autoComplete="organization"
                maxLength={160}
                required
                placeholder="Your brand"
              />
              {fieldError("company")}
            </div>
            {kind === "brand" && (
              <div>
                <label htmlFor="contact-platform" className={labelClass}>
                  Ecommerce platform
                </label>
                <Select {...field("platform")} required className="text-ink">
                  <option value="">Choose your platform</option>
                  {ecommercePlatforms.map((value) => (
                    <option key={value}>{value}</option>
                  ))}
                </Select>
                {fieldError("platform")}
                {platform === "Other" && (
                  <div className="mt-4">
                    <label
                      htmlFor="contact-otherPlatform"
                      className={labelClass}
                    >
                      Which platform?
                    </label>
                    <Input
                      {...field("otherPlatform")}
                      maxLength={120}
                      required
                      placeholder="Platform name"
                    />
                    {fieldError("otherPlatform")}
                  </div>
                )}
              </div>
            )}
            <div>
              <label htmlFor="contact-website" className={labelClass}>
                Current website
                {kind === "brand" && platform === "Not launched yet"
                  ? " (optional)"
                  : ""}
              </label>
              <Input
                {...field("website")}
                type="url"
                autoComplete="url"
                maxLength={500}
                required={
                  !(kind === "brand" && platform === "Not launched yet")
                }
                placeholder="https://yourbrand.com"
              />
              {fieldError("website")}
            </div>
            <fieldset
              aria-describedby={
                errors.products ? "contact-products-error" : undefined
              }
            >
              <legend className={labelClass}>
                What are you interested in?
              </legend>
              <div className="space-y-2">
                {contactProducts.map((product) => (
                  <label
                    key={product}
                    className="flex min-h-11 items-center gap-3 rounded-lg border border-line bg-panel px-3 py-2 text-sm text-ink cursor-pointer hover:bg-raised"
                  >
                    <input
                      type="checkbox"
                      value={product}
                      {...register("products")}
                      className="h-4 w-4 accent-brand-green focus-visible:outline-2 focus-visible:outline-offset-2"
                    />
                    {product}
                  </label>
                ))}
              </div>
              {fieldError("products")}
            </fieldset>
          </>
        )}
        <div>
          <label htmlFor="contact-message" className={labelClass}>
            {business ? "A short introduction" : "How can we help?"}
          </label>
          <Textarea
            {...field("message")}
            className="text-ink"
            rows={5}
            maxLength={4000}
            required
            placeholder={
              business
                ? "Tell us about your business and what you’d like to do with Buddify."
                : "Tell us a little about what you need."
            }
          />
          {fieldError("message")}
        </div>
        <div className="hidden" aria-hidden="true">
          <label htmlFor="contact-honeypot">Leave this field empty</label>
          <input
            id="contact-honeypot"
            name="website"
            value={honeypot}
            onChange={(event) => setHoneypot(event.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>
        <ContactTurnstile
          siteKey={siteKey}
          attempt={attempt}
          onToken={setToken}
        />
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <Button
          type="submit"
          size="lg"
          className="w-full"
          disabled={isSubmitting || !token}
        >
          {isSubmitting ? (
            <Loader2 aria-hidden="true" className="w-5 h-5 animate-spin" />
          ) : (
            <Send aria-hidden="true" className="w-5 h-5" />
          )}
          {isSubmitting ? "Sending…" : "Send enquiry"}
        </Button>
        <p className="text-xs text-muted">
          We’ll use your details to respond to this enquiry. Read our{" "}
          <a
            href="/brand-partnerships/privacy"
            className="underline hover:text-ink"
          >
            Privacy Policy
          </a>
          .
        </p>
      </fieldset>
    </form>
  );
}
